/**
 * Dev utility: inject fake social bets into the Zustand store.
 * Usage from browser console:
 *   window.__injectFakeBets(15)           — 15 current bets, mixed statuses
 *   window.__injectFakeBets(10, 8)        — 10 current + 8 previous
 *   window.__clearFakeBets()              — clear all
 */
import { useSocialStore } from 'src/store/social'
import { useGameStore } from 'src/store/game'
import type { SocialBet } from 'src/types/domain/SocialBet'
import type { BetStatus } from 'src/types/domain/BetStatus'
import type { BetDetails } from 'src/types/domain/BetDetails'

const NAMES = [
  'StreakZippy',
  'LuckyDragon',
  'NeonTiger',
  'CryptoKing',
  'GoldenEagle',
  'ShadowFox',
  'BlazeRunner',
  'IcePhoenix',
  'ThunderBolt',
  'StarGazer',
  'MoonRider',
  'SilverWolf',
  'FireStorm',
  'OceanBreeze',
  'SkyWalker',
  'DarkHorse',
  'WildCard',
  'BetMaster',
  'RoyalFlush',
  'DiamondHand',
  'SwiftArrow',
  'StormChaser',
  'GhostRider',
  'IronFist',
  'CosmicRay'
]

const COUNTRIES = ['US', 'GB', 'DE', 'JP', 'BR', 'AU', 'CA', 'FR', 'KR', 'IN', 'SE', 'DK', 'NO']

const STATUSES: BetStatus[] = ['pending', 'won', 'lost', 'cashed_out']
const PREVIOUS_STATUSES: BetStatus[] = ['won', 'lost', 'cashed_out']

function randomFrom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!
}

function randomAmount(): number {
  const amounts = [100, 200, 400, 500, 800, 1000, 2000, 2500, 5000, 10000, 25000, 50000]
  return randomFrom(amounts)
}

function getMarbleIds(): string[] {
  try {
    const gameState = useGameStore.getState()
    const gameId = gameState.currentGameId
    const game = gameId ? gameState.games.byId[gameId] : undefined
    if (
      game &&
      game.type === 'MarbleGame' &&
      Array.isArray(game.marbleIds) &&
      game.marbleIds.length > 0
    ) {
      return game.marbleIds
    }
  } catch {
    // silently fall back to no marbles
  }
  return []
}

function randomBetDetails(marbleIds: string[]): BetDetails {
  if (marbleIds.length === 0) {
    return { type: 'PickWinner' as const, selectedItems: [] }
  }

  // Randomly pick 1 marble for PickWinner
  const picked = randomFrom(marbleIds)
  return {
    type: 'PickWinner' as const,
    selectedItems: [picked]
  }
}

function makeFakeBet(
  index: number,
  raceId: string,
  marbleIds: string[],
  status?: BetStatus
): SocialBet {
  const betAmount = randomAmount()
  const multiplier = parseFloat((1.5 + Math.random() * 8).toFixed(2))
  const potentialPayout = Math.round(betAmount * multiplier)
  const finalStatus = status ?? randomFrom(STATUSES)
  const isWin = finalStatus === 'won' || finalStatus === 'cashed_out'
  const cashoutMult =
    finalStatus === 'cashed_out' ? parseFloat((1.1 + Math.random() * 3).toFixed(2)) : undefined

  return {
    id: `fake-${raceId}-${index}-${Date.now()}`,
    playerDisplayName: randomFrom(NAMES),
    playerCountry: randomFrom(COUNTRIES),
    raceId,
    raceConfigurationId: 'fake-config',
    betAmount,
    betDetails: randomBetDetails(marbleIds),
    status: finalStatus,
    potentialPayout,
    cashoutMultiplier: cashoutMult,
    actualPayout: isWin
      ? cashoutMult
        ? Math.round(betAmount * cashoutMult)
        : potentialPayout
      : undefined,
    createdAt: new Date(Date.now() - index * 1000).toISOString()
  }
}

function injectFakeBets(currentCount = 15, previousCount = 10) {
  const state = useSocialStore.getState()
  const currentRaceId = state.raceId || 'fake-race-current'
  const prevRaceId = state.previousRaceId || 'fake-race-previous'
  const marbleIds = getMarbleIds()

  useSocialStore.setState((draft) => {
    // Add current round bets
    for (let i = 0; i < currentCount; i++) {
      const bet = makeFakeBet(i, currentRaceId, marbleIds)
      draft.allBets[bet.id] = bet
    }

    // Add previous round bets (only settled statuses)
    for (let i = 0; i < previousCount; i++) {
      const bet = makeFakeBet(i, prevRaceId, marbleIds, randomFrom(PREVIOUS_STATUSES))
      draft.previousBets[bet.id] = bet
    }

    if (!draft.previousRaceId && previousCount > 0) {
      draft.previousRaceId = prevRaceId
    }
  })

  console.log(
    `Injected ${currentCount} current + ${previousCount} previous fake bets (${marbleIds.length} marbles available)`
  )
}

function clearFakeBets() {
  useSocialStore.setState((draft) => {
    // Remove only fake bets
    for (const id of Object.keys(draft.allBets)) {
      if (id.startsWith('fake-')) delete draft.allBets[id]
    }
    for (const id of Object.keys(draft.previousBets)) {
      if (id.startsWith('fake-')) delete draft.previousBets[id]
    }
  })
  console.log('Cleared all fake bets')
}

// Attach to window for console access
if (typeof window !== 'undefined') {
  ;(window as any).__injectFakeBets = injectFakeBets
  ;(window as any).__clearFakeBets = clearFakeBets
}

export { injectFakeBets, clearFakeBets }
